/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackItemProps, IStackProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { classNamesFunction, SelectionMode } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ConnectedIntentDeployConfigs } from 'src/clu/appShell/cluDeployModelPage/components/ConnectedIntentDeployConfigs';
import { CluIntentStore, OrchestrationType } from 'src/clu/core/stores/CluIntentStore';
import { getConnectionReadableName } from 'src/clu/modules/utils/orchestrationUtils';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { ResourceAssignmentForRegionSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { DeploymentInfoScreen } from 'src/modules/modelDeploy/DeploymentInfoScreen';
import { DeploymentType } from 'src/modules/modelDeploy/DeployModelModal';

export type LinkedDeploymentType = 'slot' | 'version' | OrchestrationType;
export type OrchestrationDeployModelModalProps = BasicModalProps<{
    modelName: string;
    deploymentName: string;
    resources: ResourceAssignmentForRegionSwagger[];
}> & {
    isServiceKeyTypeValid: boolean;
    isOrchestrationProjectConnectedToLUISOrQna: boolean;
};

export enum OrchestrationDeploymentSteps {
    EnterBasicInformation = 0,
    LinkedIntentsParameters = 1
}

const messages = defineMessages({
    back: { id: 'clu.orchestrationDeployModelModal.back', defaultMessage: 'Back' },
    next: { id: 'clu.orchestrationDeployModelModal.next', defaultMessage: 'Next' },
    stagingSlot: { id: 'clu.orchestrationDeployModelModal.stagingSlot', defaultMessage: 'Staging' },
    productionSlot: { id: 'clu.orchestrationDeployModelModal.productionSlot', defaultMessage: 'Production' },
    deployModalButton: { id: 'clu.orchestrationDeployModelModal.deployModalButton', defaultMessage: 'Deploy' },
    intentNameHeader: { id: 'clu.orchestrationDeployModelModal.intentNameHeader', defaultMessage: 'Intent name' },
    deployModalTitle: { id: 'clu.orchestrationDeployModelModal.deployModalTitle', defaultMessage: 'Add a deployment' },
    deploymentParams: { id: 'clu.orchestrationDeployModelModal.deploymentParams', defaultMessage: 'Deployment parameters' },
    deploymentNameHeader: { id: 'clu.orchestrationDeployModelModal.deploymentNameHeader', defaultMessage: 'Deployment name' },
    deployModalSubBody: {
        id: 'clu.orchestrationDeployModelModal.deployModalSubBody',
        defaultMessage: 'Deploying the model will take a few minutes'
    },
    duplicatedNameErrorMessage: {
        id: 'clu.orchestrationDeployModelModal.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    },
    deployModalBody: {
        id: 'clu.orchestrationDeployModelModal.deployModalBody',
        defaultMessage: `Deploying the model allows you to use the model to get predictions at runtime.
 Before deploying your orchestration project, you need to specify some deployment parameters for
 the connected intents, so at runtime the orchestration project knows which endpoint to route to.`
    }
});

const theme = getScopedTheme('OrchestrationDeployModelModal');
const borderStyle = `1px solid ${theme.borderColor}`;

const { styleComponent } = getStylist('OrchestrationDeployModelModal');
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', () => ({ padding: '15px 20px 20px 20px', minHeight: 400 }));
const NavigatorWrapper = styleComponent<IStackItemProps>(Stack.Item)('NavigatorWrapper', () => ({
    width: 200,
    padding: 20,
    borderRight: borderStyle
}));
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { borderTop: borderStyle, padding: 0 }
});

const getStepStatus = (stepIndex: OrchestrationDeploymentSteps, activeStep: OrchestrationDeploymentSteps) => {
    if (stepIndex === activeStep) {
        return 'active';
    }

    return activeStep > stepIndex ? 'completed' : 'none';
};

export const OrchestrationDeployModelModal = withModalMounter(
    observer((props: OrchestrationDeployModelModalProps) => {
        const { hidden, onSubmit, onDismiss, disabled, isServiceKeyTypeValid, isOrchestrationProjectConnectedToLUISOrQna } = props;
        const { localize } = useLocalization();
        const luisAppTracker = useProgressTracker();
        const { isLargeOrGreater } = useResponsiveMode();
        const { dispatcher, rootStore } = useGlobalState();
        const [selectedResources, setSelectedResources] = React.useState<ResourceAssignmentForRegionSwagger[]>([]);
        const tracker = useProgressTracker({
            onComplete: () => onSubmit({ deploymentName, modelName: trainedModelName, resources: selectedResources })
        });

        const [deploymentName, setDeploymentName] = React.useState('');
        const [trainedModelName, setTrainedModelName] = React.useState('');
        const [selectedDeploymentType, setSelectedDeploymentType] = React.useState<DeploymentType>('new');
        const [activeStep, setActiveStep] = React.useState(OrchestrationDeploymentSteps.EnterBasicInformation);
        const [sortKey, setSortKey] = React.useState<SortKeyType<CluIntentStore>>({ key: 'name', order: 'asc' });
        const [deploymentInfo, setDeploymentInfo] = useUpdateState<{
            [key: string]: { deploymentType: LinkedDeploymentType; deploymentName: string };
        }>({});

        const nextBtnText =
            activeStep === OrchestrationDeploymentSteps.LinkedIntentsParameters
                ? localize(messages.deployModalButton)
                : localize(messages.next);
        const {
            intentStoreHouse,
            deploymentsStoreHouse,
            trainedModelStoreHouse,
            resourceAssignmentsStoreHouse
        } = rootStore.currentCluProjectStore;

        const deployments = deploymentsStoreHouse.items;
        const regions = resourceAssignmentsStoreHouse.items;
        const trainedModels = trainedModelStoreHouse.items;
        const linkedIntents = intentStoreHouse.items.filter(intent => Boolean(intent.orchestrationInfo));
        const isDuplicateName = selectedDeploymentType === 'new' && deployments.some(d => d.deploymentName === deploymentName);
        const steps: LuisStepperStepType[] = [
            {
                id: 'basicInfo',
                title: localize(messages.deployModalTitle),
                disabled: activeStep < OrchestrationDeploymentSteps.EnterBasicInformation,
                state: getStepStatus(OrchestrationDeploymentSteps.EnterBasicInformation, activeStep)
            },
            {
                id: 'linkedIntentsParams',
                title: localize(messages.deploymentParams),
                disabled: activeStep < OrchestrationDeploymentSteps.LinkedIntentsParameters,
                state: getStepStatus(OrchestrationDeploymentSteps.LinkedIntentsParameters, activeStep)
            }
        ];

        // Load LUIS/CLU applications in case of there is some intent connected to LUIS/CLU app.
        React.useEffect(() => {
            if (intentStoreHouse.tracker.isCompleted) {
                if (linkedIntents.some(intent => intent.orchestrationType === 'Luis')) {
                    dispatcher.dispatch('loadLuisApps', { tracker: luisAppTracker });
                }

                if (linkedIntents.some(intent => intent.orchestrationType === 'Conversation')) {
                    dispatcher.dispatch('loadCluProjects');
                }
            }
        }, [intentStoreHouse.tracker.isCompleted]);

        const onSortColumn = (column: IColumn) =>
            setSortKey({ key: column.key as keyof CluIntentStore, order: column.isSortedDescending ? 'asc' : 'desc' });

        const sortColumnObject = (field: keyof CluIntentStore) => ({
            isSorted: sortKey?.key === field,
            isSortedDescending: sortKey?.key === field && sortKey?.order === 'desc'
        });

        const handleSubmit = () =>
            dispatcher.dispatch('updateCluIntents', {
                tracker,
                toastData: {},
                intents: linkedIntents.map(intent => {
                    const intentStore = intentStoreHouse.findById(intent.name);
                    const intentDeploymentInfo = deploymentInfo[intent.name];

                    if (intent.orchestrationType === 'Conversation') {
                        return {
                            name: intent.name,
                            orchestrationInfo: { ...intent.orchestrationInfo, slotName: intentDeploymentInfo.deploymentName }
                        };
                    }

                    return {
                        name: intent.name,
                        orchestrationInfo: {
                            ...intentStore.orchestrationInfo,
                            slotName:
                                intentDeploymentInfo?.deploymentType === 'slot'
                                    ? intentDeploymentInfo?.deploymentName
                                    : `v${intentDeploymentInfo?.deploymentName}`
                        }
                    };
                })
            });

        const onDeploymentTypeChange = (newType: DeploymentType) => {
            setDeploymentName('');
            setSelectedDeploymentType(newType);
        };

        const onDeploymentNameChange = (intent: CluIntentStore, dType: LinkedDeploymentType, dName: string) =>
            setDeploymentInfo({ [intent.id]: { deploymentType: dType, deploymentName: dName } });

        const columns: IColumn[] = [
            {
                minWidth: 100,
                key: 'name',
                isRowHeader: true,
                isResizable: true,
                ...sortColumnObject('name'),
                name: localize(messages.intentNameHeader),
                onRender: (intent: CluIntentStore) => (
                    <Stack>
                        <LuisText fontWeight={500}>{intent.name}</LuisText>
                        <LuisText variant="xSmall">({getConnectionReadableName(intent.orchestrationType)})</LuisText>
                    </Stack>
                )
            },
            {
                minWidth: 150,
                isResizable: true,
                key: 'deploymentName',
                name: localize(messages.deploymentNameHeader),
                onRender: (intent: CluIntentStore) => (
                    <ConnectedIntentDeployConfigs
                        intent={intent}
                        deploymentName={deploymentInfo[intent.id]?.deploymentName}
                        onDeploymentConfigsChange={(dType, dName) => onDeploymentNameChange(intent, dType, dName)}
                    />
                )
            }
        ];

        const onStepClick = (stepKey: string) => {
            let step: OrchestrationDeploymentSteps;

            switch (stepKey) {
                case 'basicInfo':
                    step = OrchestrationDeploymentSteps.EnterBasicInformation;
                    break;
                case 'linkedIntentsParams':
                default:
                    step = OrchestrationDeploymentSteps.LinkedIntentsParameters;
                    break;
            }

            setActiveStep(step);
        };

        const getStepContent = () => {
            switch (activeStep) {
                case OrchestrationDeploymentSteps.EnterBasicInformation:
                    return (
                        <DeploymentInfoScreen
                            regions={regions}
                            deployments={deployments}
                            trainedModels={trainedModels}
                            deploymentName={deploymentName}
                            trainedModelName={trainedModelName}
                            onChangeDeploymentName={setDeploymentName}
                            isServiceKeyTypeValid={isServiceKeyTypeValid}
                            onChangeTrainedModelName={setTrainedModelName}
                            selectedDeploymentType={selectedDeploymentType}
                            onDeploymentTypeChange={onDeploymentTypeChange}
                            onChangeResourceIds={resources => setSelectedResources(resources)}
                            isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
                            deploymentNameErrorMessage={isDuplicateName && localize(messages.duplicatedNameErrorMessage)}
                        />
                    );
                case OrchestrationDeploymentSteps.LinkedIntentsParameters:
                    return (
                        <Stack tokens={{ childrenGap: 22 }}>
                            <LuisText>{localize(messages.deployModalBody)} </LuisText>

                            <LuisText>{localize(messages.deployModalSubBody)}</LuisText>

                            {linkedIntents.length && (
                                <StackItem styles={{ root: { marginTop: '8px !important' } }}>
                                    <Table
                                        columns={columns}
                                        items={linkedIntents}
                                        onSortColumn={onSortColumn}
                                        selectionMode={SelectionMode.none}
                                    />
                                </StackItem>
                            )}
                        </Stack>
                    );
                default:
            }
        };

        const isStepValid = () => {
            switch (activeStep) {
                case OrchestrationDeploymentSteps.EnterBasicInformation:
                    return Boolean(deploymentName) && Boolean(trainedModelName) && !isDuplicateName;
                case OrchestrationDeploymentSteps.LinkedIntentsParameters:
                    /**
                     * The modal is not valid in the following cases:
                     * 1. If there is intent that still doesn't have deployment info.
                     * 2. If there is intent that has invalid deployment info.
                     */
                    return (
                        Object.keys(deploymentInfo).length ===
                            linkedIntents.filter(intent => intent.orchestrationType !== 'QuestionAnswering').length &&
                        Object.values(deploymentInfo).reduce(
                            (acc, val) => acc && Boolean(val.deploymentType) && Boolean(val.deploymentName),
                            true
                        )
                    );
                default:
            }
        };

        const onNextStep = () => {
            if (activeStep === OrchestrationDeploymentSteps.LinkedIntentsParameters) {
                handleSubmit();

                return;
            }

            setActiveStep(activeStep + 1);
        };

        return (
            <BasicModal
                minWidth={850}
                hidden={hidden}
                onDismiss={onDismiss}
                onSubmit={handleSubmit}
                shouldRenderFooter={false}
                doneButtonText={localize(messages.deployModalButton)}
                disabled={luisAppTracker.isRunning || tracker.isRunning || disabled}
                dialogContentProps={{ title: localize(messages.deployModalTitle), styles: dialogContentStyles }}>
                <Stack horizontal={isLargeOrGreater}>
                    <NavigatorWrapper>
                        <LuisStepper horizontal={!isLargeOrGreater} steps={steps} onStepClick={onStepClick} />
                    </NavigatorWrapper>

                    <Stack styles={{ root: { flex: 1 } }} tokens={{ childrenGap: 10 }}>
                        <ContentWrapper>{getStepContent()}</ContentWrapper>
                        <Stack
                            horizontal
                            horizontalAlign="space-between"
                            tokens={{ padding: 16 }}
                            styles={{ root: { borderTop: borderStyle } }}>
                            <Stack horizontal tokens={{ childrenGap: 8 }}>
                                {activeStep !== OrchestrationDeploymentSteps.EnterBasicInformation && (
                                    <DefaultButton onClick={() => setActiveStep(activeStep - 1)}>{localize(messages.back)}</DefaultButton>
                                )}

                                <PrimaryButton disabled={!isStepValid()} onClick={onNextStep}>
                                    {nextBtnText}
                                </PrimaryButton>
                            </Stack>

                            <DefaultButton onClick={onDismiss}>{localize(globalMessages.cancel)}</DefaultButton>
                        </Stack>
                    </Stack>
                </Stack>
            </BasicModal>
        );
    })
);
